import { DictType } from '@pma/types'
import { useEffect, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/common/command'
import { Popover, PopoverContent, PopoverTrigger } from '@components/common/popover'
import { cn, getUserFromId } from '@pma/utils/helpers'
import { Button } from '@components/common/button'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { useAuth } from '@stores/auth-store'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { EDIT_USER_ORG_PARAMETER_ENDPOINT, SERVER_URL } from '@pma/utils/api-routes'
import useTranslator from '@hooks/use-translator'
import { Separator } from '@components/common/separator'
import { toast } from '@hooks/use-toast'

interface Props {
  managerId?: string
  refetch: CallableFunction
  userId: string
}
const ManagerSelector = ({ managerId, userId, refetch }: Props) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(managerId)

  const { authConfig, signIn } = useAuth()

  const { translate, locale } = useTranslator()

  const isHebrew = locale === 'he'

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const orgId = authConfig?.last_organization_id
  const organizationUsers = authConfig?.organizationUsers

  const updateUserDetails = async (user: DictType) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,

      {
        user_id: userId,
        manager_id: user?.id,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const updateUserManagerMutation = useMutation({
    mutationKey: [`manager`],
    mutationFn: updateUserDetails,
    onSuccess: (data) => {
      setOpen(false)

      refetch && refetch()
    },
    onError: (error: DictType) => {
      toast({
        title: translate('Error'),
        description: error?.response?.data.message ||
          `${translate('Error occurred while updating')} ${translate('manager')}`,
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    managerId && setValue(managerId)
  }, [managerId])

  const availableUsers =
    organizationUsers?.length > 0 && organizationUsers?.filter((u: DictType) => u.name)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          role='combobox'
          aria-expanded={open}
          className='flex h-8 w-full items-center justify-start p-1 text-left text-muted-foreground'
        >
          {managerId
            ? getUserFromId(managerId, organizationUsers)?.name
            : translate('Select Manager')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command>
          <CommandInput placeholder={translate('Search for user') + '...'} />
          <CommandList className={cn(isHebrew ? 'rtl' : 'ltr')}>
            <CommandEmpty>{translate('No users found')}.</CommandEmpty>
            <CommandGroup>
              {availableUsers?.map((user: DictType) => (
                <CommandItem
                  key={user.id}
                  value={user?.name}
                  onSelect={() => {
                    setValue(user?.name)
                    updateUserManagerMutation.mutate(user)
                  }}
                  className={cn('my-1 flex flex-col items-start text-left text-black')}
                >
                  {user.name}
                  <Separator className='mt-1' />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default ManagerSelector
